/* People. Almost entirely sync-owned, which is the interesting part: the drawer
   has to make it obvious that Notes is the only field editing will survive the
   02:00 Function (§10 rule 1). */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDepotScope, scoped } from '../../hooks/useDepotScope';
import { useDepots } from '../../hooks/useDepots';
import { useOnline } from '../../hooks/useOnline';
import { recordHref, useOpenRecord } from '../../hooks/useOpenRecord';
import { daysUntil, fmtDate, initials, moneyPrecise } from '../../lib/format';
import type { IPerson, Tone } from '../../models';
import { qk } from '../../query/keys';
import { BulkBar, useBulk } from './BulkBar';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Capped,
  Controls,
  DataTable,
  Drawer,
  EmptyState,
  KeyValue,
  Panel,
  RelationCard,
  SearchBox,
  Section,
  Select,
  SummaryTile,
  SummaryTiles,
  TableSkeleton,
  type Column,
  LoadError,
} from '../ui';
import { AccessPanel } from './AccessPanel';
import { EditDrawer } from './EditDrawer';
import { Explain } from './Explain';
import { ACCOUNT_KINDS, personFields, valuesSeen } from '../../lib/registerFields';
import {
  isDormant,
  isMissingFromSource,
  isRealPerson,
  isLeaver,
  isUnclassified,
  mfaFor,
  offboardingGaps,
  retentionDue,
  statusReading,
} from '../../lib/registers';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/* Not a Kind, so it cannot collide with one — the Select needs a value and the
   thing being selected is the absence of a value. */
const UNCLASSIFIED = '(unclassified)';

/** The views offered by the People list's lens select. 'tidy' is the dashboard's
    "Accounts to tidy up": offboarding unfinished or dormant, so its count matches. */
const LENSES = ['all', 'unfinished', 'due', 'dormant', 'gone', 'tidy'] as const;

/* What can be set in bulk, and what is true about each.
 *
 * Depot and Kind are Hird's own columns, so a bulk write to either stands.
 *
 * Status is yours, except for a User. Every night statusFor recomputes a User's
 * status from whether the account is enabled in Entra: disabled is a Leaver, and an
 * enabled one keeps Starter if it has it and is Active otherwise. For any other type
 * (a shared mailbox, a service account, a licensed mailbox) sign-in being blocked says
 * nothing about a person, so the sync leaves the status as it is and records the
 * sign-in separately in its own column. So Active and Starter both stand, which is how
 * a Starter is moved on and how a shared mailbox is put right. Leaver is not offered
 * here: for a User it follows the account, and a converted leaver's mailbox keeps the
 * Leaver they already have.
 *
 * This used to offer Starter alone, on the belief that Active would not stand. It
 * does, and the result was that nobody could be moved out of Starter at all: the
 * sync deliberately leaves it "for a person to move on", and there was no way to. */
const BULK_FIELDS = {
  kind: {
    label: 'Account type',
    options: ACCOUNT_KINDS,
    note: 'Account type is yours — the nightly sync never overwrites it.',
  },
  loc: {
    label: 'Depot',
    options: [] as string[], // filled from the depot service, which knows the ids
    note: 'Depot is yours — Entra has no idea where anybody works, so nothing overwrites this.',
  },
  status: {
    label: 'Status',
    options: ['Active', 'Starter'],
    note: 'Active and Starter stand. For a User, Leaver follows sign-in being blocked in Entra; for any other account type the status is yours and the sync leaves it alone.',
  },
} as const;

/** A retention date that has arrived, or passed. Not "soon" — an account you
    could have deleted last month is the one worth surfacing, and a warning that
    fires early just teaches people to leave it alone. */
function dueSoon(due: string): boolean {
  return due !== '' && daysUntil(due) <= 0;
}

const STATUS_TONE: Record<IPerson['status'], Tone> = {
  Active: 'ok',
  Starter: 'info',
  Leaver: 'muted',
};

/* Deleted upstream, and the register is now the only record of the account.
   Shown as its own badge rather than folded into Status, because a leaver and a
   deleted account need opposite things: a leaver's mailbox is still there to be
   dealt with, and a deleted one took everything with it. */
function goneBadge(person: { missingSince?: string }) {
  return isMissingFromSource(person) ? (
    <Badge tone="bad" title={`No account in Entra since ${fmtDate(person.missingSince!)}`}>
      Deleted in 365
    </Badge>
  ) : null;
}

export function PeopleScreen() {
  const svc = useServices();
  const { scope } = useDepotScope();
  const online = useOnline();
  const qc = useQueryClient();

  /* Filters can arrive on the address, so a dashboard card can open the list already
     narrowed to what the card counted: /people?status=Active&kind=User. A card that
     says 101 and opens a list of 180 invites the question of which 101. Only values
     this screen actually offers are taken, so a stale or mistyped link shows everything
     rather than an empty table with no explanation. */
  const [params] = useSearchParams();
  const fromAddress = (name: string, allowed: readonly string[]): string => {
    const v = params.get(name);
    return v && allowed.includes(v) ? v : 'all';
  };

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState(() => fromAddress('status', ['Active', 'Starter', 'Leaver']));
  /* Whether the account can sign in: its own question, no longer folded into Status. */
  const [signIn, setSignIn] = useState(() => fromAddress('signin', ['Enabled', 'Blocked']));
  const [lens, setLens] = useState(() => fromAddress('lens', LENSES));
  const [kind, setKind] = useState(() => fromAddress('kind', [...ACCOUNT_KINDS, UNCLASSIFIED]));
  const [dept, setDept] = useState('all');

  /* Already on this page when a link arrives (a card clicked from here, or the back
     button): the initial values above would not run again. Only what the address
     actually says is applied, so a link with no filters does not clear yours. */
  useEffect(() => {
    if (params.has('status')) setStatus(fromAddress('status', ['Active', 'Starter', 'Leaver']));
    if (params.has('signin')) setSignIn(fromAddress('signin', ['Enabled', 'Blocked']));
    if (params.has('lens')) setLens(fromAddress('lens', LENSES));
    if (params.has('kind')) setKind(fromAddress('kind', [...ACCOUNT_KINDS, UNCLASSIFIED]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const [selected, setSelected] = useState<IPerson | null>(null);
  const [notes, setNotes] = useState('');
  /* The detail drawer keeps licences, groups and assigned devices, which a
     generic form cannot show. Add and edit open the shared drawer alongside. */
  const [editing, setEditing] = useState<IPerson | null>(null);
  const [adding, setAdding] = useState(false);

  /* Bulk classification. Eighty accounts arrived from the sync with no Kind, and
     blank means unclassified rather than User — so the head-count reads low and
     the Dormant lens cannot see any of them. Clearing that one record at a time
     is eighty round trips through a drawer.

     Only Kind. It is Hird's own column, so a bulk write here is not undone at
     02:00; doing the same to a sync-owned field would look like it worked and
     be gone by morning. */

  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const skus = useQuery({ queryKey: qk.licences, queryFn: () => svc.licences.skus() });

  const saveNotes = useMutation({

    meta: { ok: 'Notes saved' },
    mutationFn: ({ id, value }: { id: string; value: string }) => svc.people.saveNotes(id, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.people });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setSelected(null);
    },
  });

  const closeEditor = () => {
    setEditing(null);
    setAdding(false);
  };

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.people });
    void qc.invalidateQueries({ queryKey: qk.audit });
    closeEditor();
    setSelected(null);
  };

  const save = useMutation({

    meta: { ok: 'Person saved' },
    mutationFn: (values: Record<string, unknown>) => {
      const patch = values as unknown as Omit<IPerson, 'id'>;
      return editing ? svc.people.update(editing.id, patch) : svc.people.create(patch);
    },
    onSuccess: refresh,
  });

  /* One at a time rather than in parallel, and a failure on one does not stop
     the rest — the same bargain the sync's apply makes. Eighty simultaneous
     PATCHes is how you find out what SharePoint's throttling looks like, and a
     half-applied batch that says which half is far more useful than an
     all-or-nothing that leaves you guessing. */
  /* Deleting rows, not accounts.
   *
   *  Worth being plain about, because the two are easy to confuse on a screen
   *  headed Accounts: this removes the row from Ledger and does nothing at
   *  all in Microsoft 365. An account that still exists gets a new row at the
   *  next sync — people are created now, so the row would be back within four
   *  hours, blank, having lost whatever notes were on it.
   *
   *  Which makes this useful for exactly one thing: the rows whose account is
   *  already gone. The confirmation says so rather than letting somebody find
   *  out at 02:00.
   *
   *  One at a time with failures collected, like every other bulk action here —
   *  a half-applied batch that names its half beats an all-or-nothing that
   *  leaves you guessing. */


  const removePerson = useMutation({


    meta: { ok: 'Person deleted' },
    mutationFn: (id: string) => svc.people.remove(id),
    onSuccess: refresh,
  });

  const departments = useMemo(
    () => [...new Set((people.data ?? []).map((p) => p.dept))].sort(),
    [people.data],
  );

  /* Computed once for the whole list rather than per row, because the lens
     filter, the column and the head-line count all ask the same question and
     recomputing it three times per person per keystroke is three times the work
     for the same answer. */
  const gapsById = useMemo(() => {
    const byPerson = new Map<string, number>();
    for (const d of devices.data ?? []) {
      if (d.user) byPerson.set(d.user, (byPerson.get(d.user) ?? 0) + 1);
    }
    return new Map(
      (people.data ?? []).map((p) => [p.id, offboardingGaps(p, byPerson.get(p.id) ?? 0)]),
    );
  }, [people.data, devices.data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scoped(people.data ?? [], scope).filter((p) => {
      if (status !== 'all' && p.status !== status) return false;
      if (signIn !== 'all' && (p.signIn ?? '') !== signIn) return false;
      // 'Unclassified' is a filter over absence, so it cannot be a value match.
      if (kind === UNCLASSIFIED && !isUnclassified(p)) return false;
      if (kind !== 'all' && kind !== UNCLASSIFIED && p.kind !== kind) return false;
      if (dept !== 'all' && p.dept !== dept) return false;
      if (lens === 'unfinished' && gapsById.get(p.id)!.length === 0) return false;
      if (lens === 'dormant' && !isDormant(p)) return false;
      if (lens === 'tidy' && gapsById.get(p.id)!.length === 0 && !isDormant(p)) return false;
      if (lens === 'gone' && !isMissingFromSource(p)) return false;
      if (lens === 'due' && !(isLeaver(p) && dueSoon(retentionDue(p)))) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.upn.toLowerCase().includes(q) ||
        (p.email ?? '').toLowerCase().includes(q) ||
        p.title.toLowerCase().includes(q) ||
        p.dept.toLowerCase().includes(q)
      );
    });
  }, [people.data, scope, status, kind, dept, lens, query, gapsById]);

  /* Arriving from another register — a group's member list, a device's assigned
     user — with ?open= naming who to show. Matched on id or sign-in address, so
     a link can carry whichever the source had. */
  useOpenRecord(
    people.data ?? [],
    (person, value) => person.id === value || person.upn.toLowerCase() === value.toLowerCase(),
    (person) => {
      setSelected(person);
      setNotes(person.notes);
    },
  );

  const skuById = useMemo(() => new Map((skus.data ?? []).map((k) => [k.id, k])), [skus.data]);
  const depotName = (id: string) => svc.depots.name(id);
  const depots = useDepots();

  const bulk = useBulk({
    rows: people.data ?? [],
    idOf: (p) => p.id,
    nameOf: (p) => p.name,
    fields: {
      kind: {
        label: BULK_FIELDS.kind.label,
        options: ACCOUNT_KINDS.map((k) => ({ value: k, label: k })),
        note: BULK_FIELDS.kind.note,
      },
      loc: {
        label: BULK_FIELDS.loc.label,
        // Filled from the depot service, which is the only thing that knows the ids.
        options: depots.map((d) => ({ value: d.id, label: d.name })),
        note: BULK_FIELDS.loc.note,
      },
      status: {
        label: BULK_FIELDS.status.label,
        options: BULK_FIELDS.status.options.map((o) => ({ value: o, label: o })),
        note: BULK_FIELDS.status.note,
      },
    },
    update: (id, patch) => svc.people.update(id, patch as Partial<IPerson>),
    remove: (id) => svc.people.remove(id),
    invalidate: [qk.people, qk.audit],
  });

  const devicesOf = (personId: string) => (devices.data ?? []).filter((d) => d.user === personId);

  const columns: Column<IPerson>[] = [
    {
      key: 'name',
      header: 'Name',
      sortBy: (p) => p.name,
      render: (p) => (
        <div className={s.person}>
          <div className={`${s.chip} ${isLeaver(p) ? '' : s.chipBlue}`}>{initials(p.name)}</div>
          <div>
            <div className={s.cellMain}>{p.name}</div>
            {/* Email, because that is what you would actually use. The UPN is
                the sync's match key and lives on the record, labelled as such
                so the two are never mistaken for each other. */}
            <div className={s.cellSub}>{p.email || p.upn}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'title',
      priority: 2,
      header: 'Role',
      sortBy: (p) => p.title,
      render: (p) => (
        <>
          <div>{p.title}</div>
          <div className={s.cellSub}>{p.dept}</div>
        </>
      ),
    },
    { key: 'loc', header: 'Depot', sortBy: (p) => p.loc, render: (p) => depotName(p.loc) },
    {
      key: 'status',
      header: 'Status',
      sortBy: (p) => p.status,
      /* Reads what the account is, not just what accountEnabled says. A blocked
         service account is not a leaver — the blocking is its normal state. */
      render: (p) => {
        const st = statusReading(p);
        /* The deleted badge sits beside the status rather than replacing it: the
           account's last known state is still worth reading, and losing it would
           make the row say less than it did before. */
        return (
          <>
            <Badge tone={st.tone}>{st.label}</Badge>
            {goneBadge(p)}
          </>
        );
      },
    },
    {
      key: 'signIn',
      priority: 3,
      header: 'Sign-in',
      sortBy: (p) => p.signIn ?? '',
      /* Entra's own answer, shown plainly. This is the column a shared mailbox is
         read from: blocked, and not a leaver. */
      render: (p) =>
        p.signIn ? (
          <Badge tone={p.signIn === 'Blocked' ? 'muted' : 'ok'}>{p.signIn}</Badge>
        ) : (
          <span className={s.hint}>—</span>
        ),
    },
    {
      key: 'lic',
      priority: 3,
      header: 'Licences',
      sortBy: (p) => p.lic.length,
      render: (p) => (
        <div className={s.tags}>
          {p.lic.slice(0, 3).map((id) => (
            <span key={id} className={s.lic}>
              {skuById.get(id)?.short ?? id}
            </span>
          ))}
          {p.lic.length > 3 && <span className={s.lic}>+{p.lic.length - 3}</span>}
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Type',
      // Unclassified sorts last: it is the pile of work, not a type of account.
      sortBy: (p) => (isUnclassified(p) ? '￿' : p.kind ?? ''),
      /* Always shown, never blank — '—' would leave you working out whether it
         meant "a person" or "nobody has said yet", and those are now genuinely
         different answers. Amber rather than grey because it is a prompt: the
         sync creates these, and a human still has to say what they are. */
      render: (p) =>
        isUnclassified(p) ? (
          <Badge tone="warn" title="Found by the sync. Nobody has said what this account is yet.">
            Unclassified
          </Badge>
        ) : (
          <Badge tone={isRealPerson(p) ? 'muted' : 'purple'}>{p.kind}</Badge>
        ),
    },
    {
      key: 'housekeeping',
      priority: 3,
      header: 'Housekeeping',
      /* One column for three different unfinished states, because they are all
         the same instruction — somebody still has to do something about this
         account — and three sparse columns would each look empty. */
      sortBy: (p) => -(gapsById.get(p.id)?.length ?? 0),
      render: (p) => {
        const gaps = gapsById.get(p.id) ?? [];
        const due = retentionDue(p);
        if (gaps.length > 0) {
          return (
            <Badge tone="bad" title={gaps.map((g) => g.detail).join(' · ')}>
              {gaps.length} to clear
            </Badge>
          );
        }
        if (isLeaver(p)) {
          return due ? (
            <Badge tone={dueSoon(due) ? 'warn' : 'muted'}>
              {dueSoon(due) ? 'Safe to delete' : `Keep to ${fmtDate(due)}`}
            </Badge>
          ) : (
            <Badge tone="muted" title="No leaving date recorded, so no retention clock is running.">
              No date
            </Badge>
          );
        }
        return isDormant(p) ? (
          <Badge tone="warn" title={p.lastSignIn ? `Last signed in ${fmtDate(p.lastSignIn)}` : 'No sign-in ever recorded'}>
            Dormant
          </Badge>
        ) : (
          <span className={s.cellSub}>—</span>
        );
      },
    },
    {
      key: 'mfa',
      priority: 2,
      header: 'MFA',
      sortBy: (p) => p.mfa,
      render: (p) => {
        const mfa = mfaFor(p);
        return <Badge tone={mfa.tone}>{mfa.label}</Badge>;
      },
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Accounts</h1>
          <p>
            Every account and what it holds — people, plus the shared mailboxes and service
            accounts that look like people to Microsoft 365. Synced nightly from Entra.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => { setEditing(null); setAdding(true); }}>
          New account
        </Button>
      </div>

      <Controls
        count={`${rows.length} of ${people.data?.length ?? 0}${scope !== 'all' ? ` at ${depotName(scope)}` : ''}`}
      >
        <SearchBox value={query} onChange={setQuery} placeholder="Search name, email, role or department" />
        <Select
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'Active', label: 'Active' },
            { value: 'Starter', label: 'Starter' },
            { value: 'Leaver', label: 'Leaver' },
          ]}
        />
        <Select
          label="Sign-in"
          value={signIn}
          onChange={setSignIn}
          options={[
            { value: 'all', label: 'Any sign-in' },
            { value: 'Enabled', label: 'Sign-in enabled' },
            { value: 'Blocked', label: 'Sign-in blocked' },
          ]}
        />
        <Select
          label="Kind"
          value={kind}
          onChange={setKind}
          options={[
            { value: 'all', label: 'All' },
            ...ACCOUNT_KINDS.map((k) => ({ value: k, label: k })),
            /* Last, and separate: everything above is a decision somebody made,
               this is the absence of one. It is the worklist after a sync finds
               accounts nobody has seen before. */
            { value: UNCLASSIFIED, label: 'Unclassified' },
          ]}
        />
        <Select
          label="Housekeeping"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'Everything' },
            { value: 'tidy', label: 'Needs tidying up' },
            { value: 'unfinished', label: 'Offboarding unfinished' },
            { value: 'due', label: 'Safe to delete' },
            { value: 'dormant', label: 'Dormant' },
            /* The lens this register was missing. A deleted account's row looked
               exactly like everybody else's, and the only place it was reported
               was an audit entry from 02:00 that nobody reads. */
            { value: 'gone', label: 'Deleted in Microsoft 365' },
          ]}
        />
        <Select
          label="Department"
          value={dept}
          onChange={setDept}
          options={[
            { value: 'all', label: 'All departments' },
            ...departments.map((d) => ({ value: d, label: d })),
          ]}
        />
      </Controls>



      <BulkBar
        bulk={bulk}
        onDelete={() => {
          const rows = (people.data ?? []).filter((p) => bulk.ticked.has(p.id));
          /* The ones that will simply come back. Named in the prompt rather than
             refused: there are reasons to clear a row and let the sync rebuild
             it, and it should be a decision rather than a surprise. */
          const live = rows.filter((p) => !isMissingFromSource(p));

          const warning = live.length
            ? `\n\n${live.length === 1 ? 'One of these accounts still exists' : `${live.length} of these accounts still exist`} in Microsoft 365, so the ${live.length === 1 ? 'row' : 'rows'} will be recreated at the next sync — blank, without any notes you have written.`
            : '';

          return window.confirm(
            `Remove ${rows.length} ${rows.length === 1 ? 'row' : 'rows'} from ${PRODUCT.name}?` +
              '\n\nThis deletes nothing in Microsoft 365 — it only removes the record here.' +
              warning,
          );
        }}
      />

      <Panel flush>
        {people.isLoading ? (
          <TableSkeleton />
        ) : people.isError ? (
          <LoadError error={people.error} what="people" />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(p) => p.id}
            initialSort={{ key: 'name', dir: 'asc' }}
            onRowClick={(p) => {
              setSelected(p);
              setNotes(p.notes);
            }}
            selectedKey={selected?.id}
            checked={bulk.ticked}
            onCheckedChange={bulk.setTicked}
            empty={
              <EmptyState icon="ti-user-search" title="No accounts match">
                Try clearing the filters, or widen the depot scope in the header.
              </EmptyState>
            }
          />
        )}
      </Panel>

      <Drawer
        open={selected !== null}
        title={selected?.name ?? ''}
        sub={selected ? `${selected.title} · ${selected.dept}` : undefined}
        avatar={selected ? initials(selected.name) : undefined}
        meta={
          selected && (
            <>
              <Badge tone={STATUS_TONE[selected.status]}>{selected.status}</Badge>
              <Badge tone="muted">{depotName(selected.loc)}</Badge>
              <Badge tone="info">{selected.email || selected.upn}</Badge>
            </>
          )
        }
        /* Pinned, not at the end of the body. Edit details was below the last card, so
           opening a person to change something meant scrolling to the bottom of
           everything first. */
        footer={selected ? (
          <div className={s.actions}>
            <Button
              primary
              icon="ti-device-floppy"
              disabled={!online || saveNotes.isPending || notes === selected.notes}
              onClick={() => saveNotes.mutate({ id: selected.id, value: notes })}
            >
              {saveNotes.isPending ? 'Saving…' : 'Save notes'}
            </Button>
            <Button icon="ti-pencil" disabled={!online} onClick={() => setEditing(selected)}>
              Edit details
            </Button>
            {!online && <div className={s.hint}>Editing is disabled while offline.</div>}
          </div>
        ) : undefined}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <>
            {/* The four questions somebody opens a person to answer, before any
                reading. Monthly cost is here rather than on the licences card
                because "what is this account costing" is the one that gets asked
                about people who have left, and it is a number nothing else on
                the screen adds up. */}
            <SummaryTiles>
              <SummaryTile
                icon={selected.status === 'Active' ? 'ti-circle-check' : 'ti-circle-minus'}
                value={statusReading(selected).label}
                label="Status"
                tone={statusReading(selected).tone}
              />
              <SummaryTile
                icon="ti-shield-lock"
                value={mfaFor(selected).label}
                label="MFA"
                tone={mfaFor(selected).tone}
                title={mfaFor(selected).methods.join(' + ')}
              />
              <SummaryTile
                icon="ti-license"
                value={moneyPrecise(
                  selected.lic.reduce((sum, id) => sum + (skuById.get(id)?.cost ?? 0), 0),
                )}
                label="Licences a month"
                tone={isLeaver(selected) && selected.lic.length > 0 ? 'bad' : 'muted'}
              />
              <SummaryTile
                icon="ti-device-laptop"
                value={devicesOf(selected.id).length}
                label={devicesOf(selected.id).length === 1 ? 'Device out' : 'Devices out'}
                tone={isLeaver(selected) && devicesOf(selected.id).length > 0 ? 'bad' : 'muted'}
              />
            </SummaryTiles>

            <KeyValue
              rows={[
                { section: 'Contact', key: 'Email', value: selected.email },
                {
                  section: 'Contact',
                  key: 'Sign-in address',
                  value: <span className={s.mono}>{selected.upn}</span>,
                },
                { section: 'Contact', key: 'Mobile', value: selected.mobile },

                { section: 'Role', key: 'Job title', value: selected.title },
                { section: 'Role', key: 'Department', value: selected.dept },
                { section: 'Role', key: 'Depot', value: depotName(selected.loc) },
                { section: 'Role', key: 'Manager', value: selected.mgr },
                { section: 'Role', key: 'Started', value: fmtDate(selected.start) },

                {
                  section: 'Account',
                  key: 'Sign-in',
                  value: selected.signIn ? (
                    <Badge tone={selected.signIn === 'Blocked' ? 'muted' : 'ok'}>{selected.signIn}</Badge>
                  ) : (
                    'Not known yet'
                  ),
                },
                {
                  section: 'Account',
                  key: 'Account type',
                  value: isUnclassified(selected) ? (
                    <Badge tone="warn">Unclassified</Badge>
                  ) : (
                    selected.kind
                  ),
                },
                {
                  section: 'Account',
                  key: 'MFA',
                  value: (() => {
                    const mfa = mfaFor(selected);
                    /* The record is where the detail belongs — one badge per
                       registered method, so "Authenticator and a security key"
                       and "a text message" stop looking like the same answer. */
                    if (mfa.methods.length === 0) {
                      return <Badge tone={mfa.tone}>{mfa.label}</Badge>;
                    }
                    return (
                      <div className={s.tags}>
                        {mfa.methods.map((m) => (
                          <Badge key={m} tone={mfa.tone}>
                            {m}
                          </Badge>
                        ))}
                      </div>
                    );
                  })(),
                },

                /* Housekeeping appears only when there is something to say. On
                   an active account signed into last week all three of these are
                   noise, and that is most of the register — a section that is
                   always present but usually empty trains people to skip it. */
                ...(isDormant(selected) || !selected.lastSignIn
                  ? [
                      {
                        section: 'Housekeeping',
                        key: 'Last signed in',
                        value: selected.lastSignIn ? (
                          <Badge tone="warn">{fmtDate(selected.lastSignIn)}</Badge>
                        ) : (
                          <Badge tone="warn">Never recorded</Badge>
                        ),
                      },
                    ]
                  : []),
                ...(isLeaver(selected)
                  ? [
                      {
                        section: 'Housekeeping',
                        key: isRealPerson(selected) ? 'Left on' : 'Blocked on',
                        value: selected.leftOn ? fmtDate(selected.leftOn) : 'Not recorded',
                      },
                      {
                        section: 'Housekeeping',
                        key: 'Keep until',
                        owned: true,
                        value: (() => {
                          const due = retentionDue(selected);
                          if (!due) return 'No clock — set a date to start one';
                          return (
                            <Badge tone={dueSoon(due) ? 'warn' : 'muted'}>
                              {fmtDate(due)}
                              {selected.keepUntil ? '' : ' (default)'}
                            </Badge>
                          );
                        })(),
                      },
                    ]
                  : []),
              ]}
            />

            {/* What they can open, and where it is changed.
              *
              * The same panel the onboarding flow uses for its access step. It
              * lived only there until 08/10/2026, which meant the way to see
              * what an eight-year employee could open was to go to a screen
              * called Onboarding and find them in the All lens. Nothing was
              * blocked by that; it was just in the last place anybody would
              * look. Here it is in the place they do look.
              *
              * Open by default. Collapsed it would be one more thing to find,
              * and this is a question people come to a person to answer. */}
            <Section title="Hub access and apps" defaultOpen>
              <AccessPanel graphId={selected.graphId} name={selected.name} email={selected.email} />
            </Section>

            {(gapsById.get(selected.id)?.length ?? 0) > 0 && (
              <div>
                <div className={s.sectionLabel}>Offboarding not finished</div>
                {/* Each of these is visible on some other screen. None of it is
                    visible per leaver, which is the only arrangement that
                    answers whether this person was actually wound down. */}
                <div className={s.rows}>
                  {gapsById.get(selected.id)!.map((gap) => (
                    <div key={gap.kind} className={`${s.row} ${s.rowFlush}`}>
                      <span className={s.rowIcon}>
                        <i
                          className={`ti ${
                            gap.kind === 'licence'
                              ? 'ti-license'
                              : gap.kind === 'group'
                                ? 'ti-users-group'
                                : gap.kind === 'device'
                                  ? 'ti-device-laptop'
                                  : 'ti-shield-lock'
                          }`}
                          aria-hidden
                        />
                      </span>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{gap.detail}</div>
                      </div>
                      <Badge tone="bad">To clear</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Badges rather than rows, so these three stay compact even open —
                a licence is a word, not a record. */}
            <RelationCard
              icon="ti-license"
              title="Licences"
              count={selected.lic.length}
              preview={selected.lic
                .slice(0, 3)
                .map((id) => skuById.get(id)?.short ?? skuById.get(id)?.name ?? id)
                .join(' · ')}
            >
              <div className={s.tags}>
                {selected.lic.map((id) => {
                  const sku = skuById.get(id);
                  return (
                    <Badge key={id} tone={sku?.tone ?? 'muted'}>
                      {sku?.name ?? id}
                      {sku ? ` · ${moneyPrecise(sku.cost)}/mo` : ''}
                    </Badge>
                  );
                })}
              </div>
            </RelationCard>

            <RelationCard
              icon="ti-users-group"
              title="Groups"
              count={selected.groups.length}
              preview={selected.groups.slice(0, 3).join(' · ')}
            >
              <div className={s.tags}>
                {/* The join the group register was built for, finally reachable
                    from this end: who else is in this, and what does it open. */}
                {selected.groups.map((g) => (
                  <Link key={g} to={recordHref('/groups', g)} className={s.tagLink}>
                    <Badge tone="muted">{g}</Badge>
                  </Link>
                ))}
              </div>
            </RelationCard>

            <RelationCard
              icon="ti-device-laptop"
              title="Assigned devices"
              count={devicesOf(selected.id).length}
              preview={devicesOf(selected.id).map((d) => d.tag).slice(0, 3).join(' · ')}
              defaultOpen
            >
              <div className={s.rows}>
                <Capped cap={5} noun="all">
                {devicesOf(selected.id).map((d) => (
                  <Link
                    key={d.id}
                    to={recordHref('/devices', d.id)}
                    className={`${s.row} ${s.rowFlush}`}
                  >
                    <div className={s.rowIcon}>
                      <i className="ti ti-device-laptop" aria-hidden />
                    </div>
                    <div className={s.rowMain}>
                      <div className={s.rowTitle}>{d.tag}</div>
                      <div className={s.rowSub}>
                        {d.make} {d.model}
                      </div>
                    </div>
                    <i className="ti ti-chevron-right" aria-hidden />
                  </Link>
                ))}
                </Capped>
              </div>
            </RelationCard>

            <div className={s.field}>
              <label htmlFor="person-notes">Notes</label>
              <textarea
                id="person-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything the fields above do not cover."
              />
              <Explain title="Notes is the only field here that survives the sync" icon="ti-lock-open">
                Everything above comes from Entra and is rewritten at 02:00, so an edit made here
                would be gone by morning. Notes is {PRODUCT.name}'s — say what the fields cannot.
              </Explain>
            </div>
          </>
        )}
      </Drawer>
      <EditDrawer
        open={editing !== null || adding}
        record={editing}
        fields={personFields(useDepots(), valuesSeen(people.data, ['title', 'dept']))}
        lookups={{ people: people.data ?? [], suppliers: [] }}
        noun="person"
        icon="ti-user"
        title={editing ? `Edit ${editing.name}` : 'New person'}
        onClose={closeEditor}
        onSave={(values) => save.mutate(values)}
        onDelete={editing ? () => removePerson.mutate(editing.id) : undefined}
        saving={save.isPending}
        deleting={removePerson.isPending}
        error={save.error}
      >
        <Explain title="Which of these the nightly sync overwrites">
          Job title, department, manager, licences and groups come from Entra, so anything you set
          in those holds only until 02:00. Depot, account kind, mobile, start date and notes are
          {PRODUCT.name}'s and are kept.
          <br />
          <br />
          Adding somebody by hand is for a starter with no account yet, or for a shared mailbox or
          service account you want on the register before the sync sees it.
        </Explain>
      </EditDrawer>
    </>
  );
}
